import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, Warning, XCircle, Info, FileText, Clock, CurrencyInr, ShieldCheck, ArrowRight } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ALERT_TYPE_META = {
  missing_documents: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Missing Documents' },
  preauth_pending: { icon: ShieldCheck, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Pre-Auth Pending' },
  tat_breach: { icon: Clock, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'TAT Breach' },
  denial_received: { icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Denial Received' },
  ar_90_plus: { icon: Warning, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300', label: '90+ Days Outstanding' },
  high_value_pending: { icon: CurrencyInr, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'High Value Pending' },
  settlement_breach: { icon: Warning, color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-400', label: 'Settlement Breach' },
};

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  error: 'bg-rose-100 text-rose-700 border-rose-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  info: 'bg-sky-100 text-sky-700 border-sky-200',
};

const AlertsPage = () => {
  const [data, setData] = useState({ alerts: [], total: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => { loadAlerts(); }, []);

  const loadAlerts = async () => {
    try {
      const res = await alertsApi.list();
      setData(res.data);
    } catch (err) { toast.error('Failed to load alerts'); }
    finally { setLoading(false); }
  };

  const filteredAlerts = filter === 'all' ? data.alerts : data.alerts.filter(a => a.type === filter);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <Toaster position="top-right" richColors />
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="alerts-title">Alerts & Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Real-time alerts for claims requiring attention</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(ALERT_TYPE_META).map(([type, meta]) => {
          const count = data.by_type[type] || 0;
          return (
            <Card key={type} className={`cursor-pointer transition-all border ${filter === type ? `${meta.border} ring-2 ring-offset-1 ring-${meta.color.split('-')[1]}-300` : 'border-slate-200'} hover:shadow-sm`}
              onClick={() => setFilter(filter === type ? 'all' : type)} data-testid={`alert-filter-${type}`}>
              <CardContent className="pt-4 pb-3 text-center">
                <meta.icon className={`h-6 w-6 mx-auto mb-1 ${meta.color}`} weight="duotone" />
                <p className="text-2xl font-bold text-slate-800 font-['Outfit']">{count}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{meta.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Showing filter info */}
      {filter !== 'all' && (
        <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200">
          <span className="text-sm text-slate-600">Showing: <strong>{ALERT_TYPE_META[filter]?.label}</strong> ({filteredAlerts.length})</span>
          <Button variant="ghost" size="sm" onClick={() => setFilter('all')} className="text-slate-500 hover:text-slate-700">Show All</Button>
        </div>
      )}

      {/* Alerts List */}
      <div className="space-y-3" data-testid="alerts-list">
        {filteredAlerts.length === 0 ? (
          <Card className="bg-white border border-slate-200">
            <CardContent className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto mb-3 text-emerald-300" weight="duotone" />
              <p className="text-lg font-medium text-slate-700">All Clear!</p>
              <p className="text-sm text-slate-500 mt-1">No alerts at the moment. Everything looks good.</p>
            </CardContent>
          </Card>
        ) : (
          filteredAlerts.map((alert) => {
            const meta = ALERT_TYPE_META[alert.type] || ALERT_TYPE_META.missing_documents;
            return (
              <Card key={alert.id} className={`border ${meta.border} ${meta.bg} cursor-pointer hover:shadow-md transition-all`}
                onClick={() => alert.claim_id && navigate(`/claims/${alert.claim_id}`)} data-testid={`alert-item-${alert.id}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border ${meta.border}`}>
                      <meta.icon className={`h-5 w-5 ${meta.color}`} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-slate-800">{alert.title}</p>
                        <Badge className={`${SEVERITY_COLORS[alert.severity]} border text-[10px]`}>{alert.severity}</Badge>
                        {alert.insurance_claim_no && (
                          <span className="text-[10px] font-mono text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Claim: {alert.insurance_claim_no}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">{alert.message}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <p className="text-xs text-slate-400 text-center">Alerts refresh on page load. Navigate to a claim for details.</p>
    </div>
  );
};

export default AlertsPage;
